import axios from 'axios';

/**
 * @typedef LearningLockerConfig
 * @type {object}
 * @property {string} url
 * @property {string} user
 * @property {string} pass
 */

export default class LearningLocker {
    /**
     * @param {LearningLockerConfig} config
     */
    constructor(config) {
        const { url, user, pass } = config;
        this._url = url;
        this._user = user;
        this._pass = pass;
    }

    request(method, endpoint, config = {}) {
        Logger.info('LL :: request', {method, endpoint, config})
        return axios({
            method,
            url: `${ this._url }/api/${ endpoint }`,
            auth: {
                username: this._user,
                password: this._pass,
            },
            ...config,
        });
    }

    getPersonaIdentifier(email, filter = {}) {
        Logger.info('LL :: getPersonaIdentifier', {email})
        return this.request('get', 'v2/personaidentifier', {
            params: {
                filter: {
                    'ifi.value': `mailto:${ email }`,
                    'ifi.key': 'mbox',
                    ...filter,
                },
            },
        });
    }

    async addPersonaIdentifier(personaId, email) {
        const exists = await this.getPersonaIdentifier(email, {
            persona: personaId,
        });

        if (exists.data.length) {
            return false;
        }

        Logger.info('LL :: addPersonaIdentifier', { personaId, email })
        return this.request('post', 'v2/personaidentifier', {
            data: {
                persona: personaId,
                ifi: {
                    key: 'mbox',
                    value: `mailto:${ email }`,
                },
            },
        });
    }

    deletePersonaIdentifier(id) {
        Logger.info('LL :: addPersonaIdentifier', {id})
        return this.request('delete', `v2/personaidentifier/${id}`);
    }

    async updatePersonaIdentifier(personaId, oldEmail, newEmail) {
        const oldIdentifierResponse = await this.getPersonaIdentifier(oldEmail, {
            persona: personaId,
        });

        if (!oldIdentifierResponse.data.length) {
            return {
                success: false,
                message: 'There are no identifiers using this email'
            };
        }

        const oldIdentifier = oldIdentifierResponse.data[0];

        const newIdentifierResponse = await this.addPersonaIdentifier(personaId, newEmail)
            .catch(error => error.response);

        if (newIdentifierResponse.status !== 200) {
            return {
                success: false,
                message: newIdentifierResponse.data.message,
                status: newIdentifierResponse.status,
            }
        }

        return { success: true };
    }

    addPersona(name) {
        Logger.info('LL :: addPersona', {name})
        return this.request('post', 'v2/persona', {
            data: { name },
        });
    }

    async findOrCreatePersonaId(email, name) {
        Logger.info('LL :: findOrCreatePersonaId 1', {email, name})
        const personaIdentifier = await this.getPersonaIdentifier(email);

        Logger.info('LL :: findOrCreatePersonaId 2 / personaIdentifier.data?.[0]?.persona',
            { personaIdentifier });
        if (personaIdentifier.data?.[0]?.persona) {
            return personaIdentifier.data[0].persona;
        }

        const persona = await this.addPersona(name);

        Logger.info('LL :: findOrCreatePersonaId 3 / persona.data._id',
            { personaIdentifier, persona });
        if (!persona?.data?._id) {
            return false;
        }

        const identifier = await this.addPersonaIdentifier(persona.data._id,
            email);

        Logger.info('LL :: findOrCreatePersonaId 4 / identifier?.data?.persona',
            { personaIdentifier, persona, identifier });
        if (!identifier?.data?.persona) {
            return false;
        }

        return identifier.data.persona;
    }

    async getAttributes(personaId, filters = [], after = null) {
        Logger.info('LL :: getAttributes', {personaId, filters, after})
        if (!personaId) {
            return null;
        }

        // {"personaId":"5eba96325c1f4306b3180ab0", "$or": [{"key": "Sample"},{"key": "Sample2"}]}

        return this.request('get', 'connection/personaattribute', {
            params: {
                filter: {
                    personaId: personaId,
                    ...(
                        filters.length > 0
                            ? { $or: filters.map(key => ({ key })) }
                            : {}
                    ),
                },
                after: after,
            },
        });
    }

    async updateAttribute(attributeId, value) {
        Logger.info('LL :: updateAttribute', {attributeId, value})
        if (!attributeId) {
            return null;
        }

        return this.request('patch', `v2/personaattribute/${attributeId}`, {
            data: { value },
        });
    }

    async addAttribute(personaId, key, value) {
        Logger.info('LL :: addAttribute', {personaId, key, value})

        if (!personaId) {
            return null;
        }

        return this.request('post', 'v2/personaattribute', {
            data: { personaId, key, value },
        });
    }

    async getPersonaAttributeNodes(personaId, filter) {
        // let after = null;
        // let hasNextPage = false;
        let counter = 0;
        let edges = [];
        let hasNextPage = false;
        let endCursor = null;

        // Request more attributes for as long as there is a next page
        do {
            let { data } = await this.getAttributes(
                personaId,
                filter,
                endCursor,
            );

            // hasNextPage = data?.pageInfo ?? false;
            // after = data?.pageInfo ?? null;

            ({ hasNextPage, endCursor } = data?.pageInfo);

            Logger.info('EDGES', data.edges);
            Logger.info('EDGES-',
                data.edges.map(({ node: { key, value } }) => ([
                    key,
                    value,
                ])));

            edges.push(...data.edges.map(({ node }) => node));

            counter++;

            if (counter > 50) {
                console.throw(
                    'LearningLockerStorage: The request limit has been reached for getting attributes. The user has too many attributes assigned to their persona.');
                hasNextPage = false;
                break;
            }
        } while (hasNextPage);

        return edges;
    }
}
